import { useCallback } from "react";
import { useAuthV2Store } from "./auth-store";
import { authApi } from "./auth-api";

/**
 * Hook unifié qui remplace `useAuth` de Clerk côté Pro. Renvoie une API
 * proche : `isSignedIn`, `isLoaded`, `signOut`, `getToken`.
 *
 * `getToken` est intentionnellement async : il déclenche un refresh
 * silencieux si l'access expire dans moins de 60 s.
 */
const ACCESS_REFRESH_GRACE_MS = 60_000;

export const useAuthV2 = () => {
  const accessToken = useAuthV2Store((s) => s.accessToken);
  const refreshToken = useAuthV2Store((s) => s.refreshToken);
  const accessExpiresAt = useAuthV2Store((s) => s.accessExpiresAt);
  const identity = useAuthV2Store((s) => s.identity);
  const hydrated = useAuthV2Store((s) => s.hydrated);
  const updateAccess = useAuthV2Store((s) => s.updateAccess);
  const clear = useAuthV2Store((s) => s.clear);

  const isSignedIn = Boolean(refreshToken && identity);
  const isLoaded = hydrated;

  const getToken = useCallback(async (): Promise<string | null> => {
    const state = useAuthV2Store.getState();
    if (!state.refreshToken) return null;
    const now = Date.now();
    const expiresAt = state.accessExpiresAt ?? 0;
    if (state.accessToken && expiresAt - now > ACCESS_REFRESH_GRACE_MS) {
      return state.accessToken;
    }
    try {
      const next = await authApi.refresh(state.refreshToken);
      updateAccess(next);
      return next.accessToken;
    } catch {
      state.clear();
      return null;
    }
  }, [updateAccess]);

  const signOut = useCallback(async (): Promise<void> => {
    const state = useAuthV2Store.getState();
    const at = state.accessToken;
    const rt = state.refreshToken ?? undefined;
    state.clear();
    if (at) {
      try {
        await authApi.logout({ accessToken: at, refreshToken: rt });
      } catch {
        // Best-effort : la session locale est déjà vidée.
      }
    }
  }, []);

  return {
    isSignedIn,
    isLoaded,
    identity,
    accessToken,
    getToken,
    signOut,
  };
};
